import { Button, Center, Container, Text, VStack} from '@chakra-ui/react'
import FilterContainer from '../components/filtercontainer'
import React from 'react'
import { useRef, useState, useEffect } from 'react'
import { useAppSelector } from '../store/hooks.ts'
import { isMobile } from 'react-device-detect'
import { createResults, uploadImage } from '../api/results.js'

const CENTERFACE_TEXT = `\n\nPlease center your face in the webcam\n\n`
const INTERACT_TEXT = `Please take some time to try out the camera. The "Capture" button will appear after 15 seconds.`

// Stable identity so the capture-button re-render does not re-run FilterContainer's
// camera effect (which would re-acquire the webcam and freeze the live feed).
const WEBCAM_CONSTRAINTS = { audio: false, video: { width: 315, height: 318 } }

// ?filtertest=1 — shows all filter effects at max, skips DB save and Qualtrics redirect.
const FILTER_TEST_MODE = new URLSearchParams(window.location.search).get('filtertest') === '1'

const FILTER_TEST_PARAMS = {
  whiten: 1.0,
  dermabrasion: 1.0,
  lift: 1.0,
  shave: 1.0,
  eye: 1.0,
  chin: 1.0,
  darkCircle: 1.0,
  nasolabialFolds: 1.0,
  cheekbone: 1.0,
  head: 1.0,
  eyeBrightness: 1.0,
  lip: 1.0,
  forehead: 1.0,
  nose: 1.0,
  usm: 1.0,
}

const FILTER_TEST_KEYS = new Set(Object.keys(FILTER_TEST_PARAMS))

function validateFilterTestPatch(patch) {
    const applied = {}
    const ignored = []
    const error = {}

    if (patch == null || typeof patch !== 'object' || Array.isArray(patch)) {
        return { applied, ignored, error: { _patch: 'patch must be an object' } }
    }

    for (const [key, value] of Object.entries(patch)) {
        if (!FILTER_TEST_KEYS.has(key)) {
            ignored.push(key)
            continue
        }

        if (typeof value !== 'number' || !Number.isFinite(value)) {
            error[key] = 'must be a number'
            continue
        }

        if (value < 0 || value > 1) {
            error[key] = 'must be between 0 and 1'
            continue
        }

        applied[key] = value
    }

    return { applied, ignored, error }
}

// Hard-coded parameters, set to requested values for now
let beautyParams = {
  whiten: 0.0, // The brightening effect. Value range: 0-1.
  dermabrasion: 1.0, // The smooth skin effect. Value range: 0-1.
  lift: 0.0, // The slim face effect. Value range: 0-1.
  shave: 0.0, // The face width. Value range: 0-1.
  eye: 0.0, // The big eyes effect. Value range: 0-1.
  chin: 0.0, // The chin shaping effect. Value range: 0-1.
  darkCircle: 0.9, // The dark circle effect. Value range: 0-1.
  nasolabialFolds: 0.9, // The nasolabial folds effect. Value range: 0-1.
  cheekbone: 0.0, // The cheek bone effect. Value range: 0-1.
  head: 0.0, // The head effect. Value range: 0-1.
  eyeBrightness: 0.4, // The eye brightness effect. Value range: 0-1.
  lip: 0.0, // The lip effect. Value range: 0-1.
  forehead: 0.0, // The forehead effect. Value range: 0-1.
  nose: 0.0, // The nose effect. Value range: 0-1.
  usm: 0.0, // The distinct effect. Value range: 0-1.
};

const FilterPage = () => {
    // Randomize if the filter is turned on or not
    let isNotControl = useAppSelector(state => state.survey.control_condition)
    const [filterTestBeautyParams, setFilterTestBeautyParams] = useState(FILTER_TEST_MODE ? FILTER_TEST_PARAMS : null)
    const filterTestBeautyRef = useRef(filterTestBeautyParams)

    if(FILTER_TEST_MODE){
        filterTestBeautyRef.current = filterTestBeautyParams
    }

    if(FILTER_TEST_MODE){
        beautyParams = filterTestBeautyParams
    } else if(isNotControl == 1){
        beautyParams = {
            whiten: 0.0, // The brightening effect. Value range: 0-1.
            dermabrasion: 0.0, // The smooth skin effect. Value range: 0-1.
            lift: 0.0, // The slim face effect. Value range: 0-1.
            shave: 0.0, // The face width. Value range: 0-1.
            eye: 0.0, // The big eyes effect. Value range: 0-1.
            chin: 0.0, // The chin shaping effect. Value range: 0-1.
            darkCircle: 0.0, // The dark circle effect. Value range: 0-1.
            nasolabialFolds: 0.0, // The nasolabial folds effect. Value range: 0-1.
            cheekbone: 0.0, // The cheek bone effect. Value range: 0-1.
            head: 0.0, // The head effect. Value range: 0-1.
            eyeBrightness: 0.0, // The eye brightness effect. Value range: 0-1.
            lip: 0.0, // The lip effect. Value range: 0-1.
            forehead: 0.0, // The forehead effect. Value range: 0-1.
            nose: 0.0, // The nose effect. Value range: 0-1.
            usm: 0.0, // The distinct effect. Value range: 0-1.
        };
    }
    

    
    if(FILTER_TEST_MODE){
        console.log("Filter test mode active — DB save and redirect disabled.")
    }

    // Initialize the results object. Everything here will get overwritten (unless stuff breaks)
    let results = {
        userID: "0.0.0.fake",
        qualtricsID: "init",
        timestamp: Date.now(),
        isMobile: isMobile,
        didComplete: true, // This only gets put into the database when Submit is clicked. Should never be false?
        surveyResponse: "init",
        imageURL: "init",
        filter: beautyParams
    }

    const screenshotFunctionRef = useRef(null);

    // Delay the appearance of the capture button by 15 seconds so the user
    // has time to interact with the camera first.
    const [showCaptureButton, setShowCaptureButton] = useState(false);
    useEffect(() => {
        const timer = setTimeout(() => setShowCaptureButton(true), 15000);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if(!FILTER_TEST_MODE){
            return
        }

        const getBeauty = () => ({ ...filterTestBeautyRef.current })

        window.__fauxstagramFilterTest = {
            get: getBeauty,
            apply: (patch) => {
                const validation = validateFilterTestPatch(patch)
                const nextBeauty = { ...filterTestBeautyRef.current, ...validation.applied }
                const hasApplied = Object.keys(validation.applied).length > 0

                if(hasApplied){
                    filterTestBeautyRef.current = nextBeauty
                    setFilterTestBeautyParams(nextBeauty)
                }

                return {
                    applied: hasApplied,
                    beauty: getBeauty(),
                    ignored: validation.ignored,
                    error: validation.error,
                }
            },
            reset: () => {
                filterTestBeautyRef.current = FILTER_TEST_PARAMS
                setFilterTestBeautyParams(FILTER_TEST_PARAMS)
                return { applied: true, beauty: getBeauty(), ignored: [], error: {} }
            },
        }

        console.log("Filter test console active: window.__fauxstagramFilterTest")

        return () => {
            delete window.__fauxstagramFilterTest
        }
    }, []);

    results.qualtricsID = useAppSelector(state => state.survey.qualtrics_id)

    const handleCreateResults = async(theScreenshot) => {
        if(FILTER_TEST_MODE){
            console.log("Filter test mode: skipping upload, DB save, and redirect.")
            return
        }

        // Save the photo in storage and the link to it
        const photoName = results.qualtricsID + ".png"

        // Dev build serves photos from the local /storage/ route; production build
        // uses the Azure file share. CRA sets NODE_ENV per build command.
        const imageURL = process.env.NODE_ENV === 'development'
            ? '/storage/'
            : 'https://fstaresult.file.core.windows.net/fstagramstorage/'
        results.imageURL = imageURL + photoName

        const { success: uploadSuccess, message: uploadMessage } = await uploadImage(theScreenshot, photoName)
        if (!uploadSuccess) {
            console.error("Image upload failed: " + uploadMessage)
        }

        // Assign User IP as User ID
        const res = await fetch('https://api.ipify.org?format=json')
        const data = await res.json()
        results.userID = data.ip

        // Put it into the database
        const {success, message} = await createResults(results)
        if (!success) {
            console.error("Saving survey results failed: " + message)
        }

        // TODO: navigate to the qualtrics page
        window.location.replace('https://columbia.qualtrics.com/jfe/form/SV_40GgWpx7r9UeN6K?originalID=' + results.qualtricsID + '&Q_R=' + results.qualtricsID + '&redirect=2');
    }

    return (
        <Container maxW={'container.xl'} paddingTop="15rem">
            <VStack spacing={'16px'} justifyContent={'space-between'}>
                <Center>
                    <FilterContainer beauty={beautyParams} webcamConstraints={WEBCAM_CONSTRAINTS} screenshotFunctionRef={screenshotFunctionRef} />
                </Center>
                <Text whiteSpace="pre-line" align={'center'} fontSize={"2xl"}>{CENTERFACE_TEXT}</Text>
                <Text align={'center'} fontSize={"lg"}>{INTERACT_TEXT}</Text>
                {showCaptureButton && (
                    <Center><Button colorScheme='blue' onClick={function(){
                        const theScreenshot = screenshotFunctionRef.current();
                        handleCreateResults(theScreenshot)
                    }}>Capture</Button></Center>
                )}
            </VStack>
        </Container>
    )
}

export default FilterPage